import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAusschreibungEvents } from '../../api/stellenausschreibungen';
import { BedarfStatusBadge } from '../bedarfe/BedarfStatusBadge';
import type {
  AusschreibungEvent,
  AusschreibungEventType,
} from '../../types/stellenausschreibung';
import {
  AusschreibungSpalte,
  AusschreibungSpalteConfig,
} from '../../types/stellenausschreibung';

const EVENT_CONFIG: Record<
  AusschreibungEventType,
  { icon: string; label: string; bg: string; fg: string }
> = {
  NEU_AKTIV: {
    icon: '+',
    label: 'Neu und zu besetzen',
    bg: 'bg-green-100',
    fg: 'text-green-700',
  },
  WURDE_AKTIV: {
    icon: '↑',
    label: 'Jetzt zu besetzen',
    bg: 'bg-blue-100',
    fg: 'text-blue-700',
  },
  WURDE_INAKTIV: {
    icon: '↓',
    label: 'Nicht mehr zu besetzen',
    bg: 'bg-amber-100',
    fg: 'text-amber-700',
  },
};

function formatName(event: AusschreibungEvent): string {
  const { childFirstName, childLastName } = event.bedarf;
  return childLastName ? `${childFirstName} ${childLastName}` : childFirstName;
}

/**
 * Der Ausschreibungsstand macht aus der Meldung eine Aufgabe: ohne ihn stünde hier
 * nur, dass sich etwas geändert hat, nicht, ob noch etwas zu tun ist.
 */
function StandZeile({ event }: { event: AusschreibungEvent }) {
  const { spalte, liveCount } = event.ausschreibung;

  if (!spalte) {
    // Ohne Bedarf lässt sich über den Ausschreibungsstand nichts sagen: seine
    // Anzeigen sind mit ihm gelöscht worden, ihr Fehlen ist keine Aussage.
    if (!event.bedarf.currentStatus) return null;

    // Bekannter Bedarf, aber keine Spalte heißt: nicht mehr zu besetzen und nie
    // eine Anzeige gehabt. Ohne diese Zeile stünde da nur „nicht mehr zu
    // besetzen" und man müsste raten, ob irgendwo noch etwas abzuschalten ist —
    // die Antwort „nein" läge allein im fehlenden Badge.
    return (
      <span className="mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
        war nicht ausgeschrieben
      </span>
    );
  }

  const config = AusschreibungSpalteConfig[spalte];

  const text =
    spalte === AusschreibungSpalte.AUSZUSCHREIBEN
      ? 'noch nicht ausgeschrieben'
      : spalte === AusschreibungSpalte.ZU_DEAKTIVIEREN
        ? `${liveCount} Anzeige${liveCount === 1 ? '' : 'n'} noch online`
        : spalte === AusschreibungSpalte.VEROEFFENTLICHT
          ? 'auf allen Kanälen online'
          : spalte === AusschreibungSpalte.ERLEDIGT
            ? 'abgeschaltet'
            : `${liveCount} von 3 Kanälen`;

  return (
    <span
      className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${config.bgColor} ${config.color}`}
    >
      {text}
    </span>
  );
}

function EventRow({ event }: { event: AusschreibungEvent }) {
  const cfg = EVENT_CONFIG[event.eventType];
  const timeAgo = formatDistanceToNow(new Date(event.occurredAt), {
    addSuffix: true,
    locale: de,
  });

  return (
    <li className="flex gap-3 py-3">
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-bold ${cfg.bg} ${cfg.fg}`}
        aria-label={cfg.label}
      >
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <Link
            to={`/bedarfe/${event.bedarfId}`}
            className="font-medium text-gray-900 hover:text-primary-600 truncate"
          >
            {formatName(event)}
          </Link>
          <span
            className="text-xs text-gray-400 shrink-0"
            title={new Date(event.occurredAt).toLocaleString('de-DE')}
          >
            {timeAgo}
          </span>
        </div>

        <div className="text-xs text-gray-500 mt-0.5 truncate">
          {[cfg.label, event.bedarf.schoolCityName].filter(Boolean).join(' · ')}
        </div>

        {event.statusChange && (
          <div className="flex items-center gap-2 mt-1">
            <BedarfStatusBadge status={event.statusChange.from} size="sm" />
            <span className="text-gray-400 text-xs">→</span>
            <BedarfStatusBadge status={event.statusChange.to} size="sm" />
          </div>
        )}

        <StandZeile event={event} />
      </div>
    </li>
  );
}

export function AusschreibungTicker({ days = 14 }: { days?: number }) {
  const { data, isLoading, error } = useAusschreibungEvents(days);
  const events = data?.data ?? [];

  return (
    <div className="bg-white rounded-lg shadow flex flex-col max-h-[70vh] xl:max-h-none xl:h-full">
      <div className="px-4 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">
            Was sich geändert hat
          </h2>
          {!isLoading && !error && (
            <span className="text-xs text-gray-500">{events.length}</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          Nur Bedarfe, die zu besetzen sind oder es nicht mehr sind ({days} Tage)
        </p>
      </div>
      <div className="flex-1 overflow-y-auto px-4">
        {isLoading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">Fehler beim Laden</p>
        ) : events.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Nichts Neues für die Ausschreibungen
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {events.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
